import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import type { Entry } from '../api/entries'
import { getFeed } from '../api/entries'
import { getUserSettings, updateUserSettings } from '../api/users'
import { EntryCard } from '../components/Entry/EntryCard'
import { useAuthStore } from '../store/auth'
import { usePageHeader } from '../hooks/usePageHeader'

const PAGE_SIZE = 50

const FILTER_LABELS: { key: 'feed_include_own' | 'feed_include_friends' | 'feed_include_fof' | 'feed_include_randos'; label: string }[] = [
  { key: 'feed_include_own', label: 'my posts' },
  { key: 'feed_include_friends', label: 'friends' },
  { key: 'feed_include_fof', label: 'friends of friends' },
  { key: 'feed_include_randos', label: 'randos' },
]

interface FeedFilter {
  feed_include_own: boolean
  feed_include_friends: boolean
  feed_include_fof: boolean
  feed_include_randos: boolean
}

interface FeedFiltersProps {
  filter: FeedFilter | null
  allEnabled: boolean
  someEnabled: boolean
  onToggleAll: () => void
  onToggle: (key: keyof FeedFilter) => void
}

function FeedFilters({ filter, allEnabled, someEnabled, onToggleAll, onToggle }: FeedFiltersProps) {
  const selectAllRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someEnabled && !allEnabled
    }
  }, [someEnabled, allEnabled])

  return (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer', opacity: 0.6, whiteSpace: 'nowrap' }}>
        <input
          ref={selectAllRef}
          type="checkbox"
          checked={allEnabled}
          disabled={filter === null}
          onChange={onToggleAll}
        />
        select all
      </label>
      <span style={{ opacity: 0.25, userSelect: 'none' }}>|</span>
      {FILTER_LABELS.map(({ key, label }) => (
        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input
            type="checkbox"
            checked={filter ? filter[key] : false}
            disabled={filter === null}
            onChange={() => onToggle(key)}
          />
          {label}
        </label>
      ))}
    </div>
  )
}

export function Feed() {
  const username = useAuthStore((s) => s.username)
  const [filter, setFilter] = useState<FeedFilter | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const [settingsLoaded, setSettingsLoaded] = useState(false)

  useEffect(() => {
    if (!username) return
    getUserSettings(username)
      .then((s) => {
        setFilter({
          feed_include_own: s.feed_include_own,
          feed_include_friends: s.feed_include_friends,
          feed_include_fof: s.feed_include_fof,
          feed_include_randos: s.feed_include_randos,
        })
        setSettingsLoaded(true)
      })
      .catch(console.error)
  }, [username])

  const loadFeed = useCallback(() => {
    setLoading(true)
    getFeed(PAGE_SIZE, 0)
      .then((data) => {
        setEntries(data)
        setHasMore(data.length === PAGE_SIZE)
        setOffset(PAGE_SIZE)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!username || !settingsLoaded) return
    loadFeed()
  }, [username, settingsLoaded, loadFeed])

  const allEnabled = filter !== null &&
    filter.feed_include_own && filter.feed_include_friends &&
    filter.feed_include_fof && filter.feed_include_randos

  const someEnabled = filter !== null && (
    filter.feed_include_own || filter.feed_include_friends ||
    filter.feed_include_fof || filter.feed_include_randos
  )

  const toggleFilter = useCallback(async (key: keyof FeedFilter) => {
    if (!username || !filter) return
    const updated = { ...filter, [key]: !filter[key] }
    setFilter(updated)
    try {
      await updateUserSettings(username, { [key]: updated[key] })
      loadFeed()
    } catch (err) {
      console.error(err)
      setFilter(filter)
    }
  }, [username, filter, loadFeed])

  const toggleAll = useCallback(async () => {
    if (!username || !filter) return
    const newValue = !allEnabled
    const updated = {
      feed_include_own: newValue,
      feed_include_friends: newValue,
      feed_include_fof: newValue,
      feed_include_randos: newValue,
    }
    setFilter(updated)
    try {
      await updateUserSettings(username, updated)
      loadFeed()
    } catch (err) {
      console.error(err)
      setFilter(filter)
    }
  }, [username, filter, allEnabled, loadFeed])

  const feedActions = useMemo(() => (
    <FeedFilters
      filter={filter}
      allEnabled={allEnabled}
      someEnabled={someEnabled}
      onToggleAll={toggleAll}
      onToggle={toggleFilter}
    />
  ), [filter, allEnabled, someEnabled, toggleAll, toggleFilter])

  usePageHeader('feed', feedActions)

  if (!username) return <Navigate to="/login" replace />

  function loadMore() {
    getFeed(PAGE_SIZE, offset)
      .then((data) => {
        setEntries((prev) => [...prev, ...data])
        setHasMore(data.length === PAGE_SIZE)
        setOffset((prev) => prev + PAGE_SIZE)
      })
      .catch(console.error)
  }

  return (
    <div style={{ marginTop: '-1rem' }}>
      {loading ? (
        <p>loading…</p>
      ) : entries.length === 0 ? (
        <p style={{ opacity: 0.5 }}>
          {filter && !filter.feed_include_own && !filter.feed_include_friends && !filter.feed_include_fof && !filter.feed_include_randos
            ? 'select at least one group above to see entries'
            : 'nothing to show'}
        </p>
      ) : (
        <>
          {entries.map((e) => (
            <EntryCard
              key={e.id}
              entry={e}
              onArchived={(id) => setEntries((prev) => prev.filter((x) => x.id !== id))}
            />
          ))}
          {hasMore && (
            <button type="button" onClick={loadMore} style={{ marginTop: '1rem' }}>
              load more
            </button>
          )}
        </>
      )}
    </div>
  )
}
